import gql from "graphql-tag";
import { useQuery, useLazyQuery, QueryHookOptions } from "@apollo/client";
import { replacePlusFromURLPath } from "@ugg/shared/utils/url-query-params";
import { SummonerProfileInitSimple, PlayerInfo } from "./profile-init";
import { MatchSummary, MatchSummaryInfo } from "./match-summaries";
import { RankScore } from "./profile-ranks";
import { Tag, SummonerTag } from "./summoner-tags";
import { PerformanceScoreSchema, PerformanceScore } from "./performance";

export const GET_SINGLE_MATCH_DATA = gql`
  query match(
    $matchId: String!,
    $regionId: String!,
    $summonerName: String!,
    $version: String!
  ) {
    match(
      matchId: $matchId,
      regionId: $regionId,
      summonerName: $summonerName,
      version: $version
    ) {
      allPlayerRanks {
        rankScores {
          lastUpdatedAt
          losses
          lp
          queueType
          rank
          role
          seasonId
          tier
          wins
        }
        summonerName
      }
      historicalData {
        kaDifferenceFrames {
          oppValue
          timestamp
          youValue
        }
        xpDifferenceFrames {
          oppValue
          timestamp
          youValue
        }
        teamOneOverview {
          bans
          baronKills
          dragonKills
          gold
          inhibitorKills
          kills
          riftHeraldKills
          towerKills
        }
        teamTwoOverview {
          bans
          baronKills
          dragonKills
          gold
          inhibitorKills
          kills
          riftHeraldKills
          towerKills
        }
        runes
        skillPath
        statShards
        accountIdV3
        csDifferenceFrames {
          oppValue
          timestamp
          youValue
        }
        finishedItems{
          itemId
          timestamp
          type
        }
        goldDifferenceFrames {
          oppValue
          timestamp
          youValue
        }
        itemPath {
          itemId
          timestamp
          type
        }
        matchId
        postGameData {
          assists
          augments
          carryPercentage
          championId
          cs
          damage
          damageTaken
          deaths
          gold
          items
          jungleCs
          keystone
          kills
          level
          role
          subStyle
          summonerName
          summonerSpells
          teamId
          wardsPlaced
          level
        }
        primaryStyle
        queueType
        regionId
        subStyle
        summonerName
      }
      matchSummary {
        ${MatchSummaryInfo}
      }
      playerInfo {
        ${PlayerInfo}
      }
      performanceScore {
        hardCarry
        teamplay
        summonerName
      }
      winningTeam
    }
  }
`;

export interface DifferenceFrame {
  oppValue: number;
  timestamp: number;
  youValue: number;
}

interface TeamOverview {
  bans: number[];
  baronKills: number;
  dragonKills: number;
  gold: number;
  inhibitorKills: number;
  kills: number;
  riftHeraldKills: number;
  towerKills: number;
}

export interface SummonerMatch {
  winningTeam: number;
  allPlayerRanks: Array<{
    rankScores: RankScore[];
    summonerName: string;
  }>;
  matchSummary: MatchSummary;
  playerInfo: SummonerProfileInitSimple["playerInfo"];
  performanceScore: Array<{
    hardCarry: number;
    teamplay: number;
    summonerName: string;
  }>;
  historicalData: {
    csDifferenceFrames: DifferenceFrame[];
    xpDifferenceFrames: DifferenceFrame[];
    goldDifferenceFrames: DifferenceFrame[];
    kaDifferenceFrames: DifferenceFrame[];
    teamOneOverview: TeamOverview;
    teamTwoOverview: TeamOverview;
    runes: number[];
    skillPath: number[];
    statShards: number[];
    finishedItems: Array<{
      itemId: number;
      timestamp: number;
      type: number;
    }>;
    itemPath: Array<{
      itemId: number;
      timestamp: number;
      type: number;
    }>;
    matchId: string;
    postGameData: Array<{
      assists: number;
      augments: number[];
      carryPercentage: number;
      championId: number;
      cs: number;
      damage: number;
      damageTaken: number;
      deaths: number;
      gold: number;
      items: number[];
      jungleCs: number;
      keystone: number;
      kills: number;
      level: number;
      role: number;
      subStyle: number;
      summonerName: string;
      summonerSpells: number[];
      teamId: number;
      wardsPlaced: number;
    }>;
    primaryStyle: number;
    queueType: string;
    regionId: string;
    subStyle: number;
    summonerName: string;
  };
}

export interface SummonerMatchData {
  match: SummonerMatch;
}

const getSummonerMatchVariables = (region: string, summonerName: string, patch: string, matchId: string) => {
  return {
    regionId: region || "",
    summonerName: replacePlusFromURLPath(summonerName) || "",
    matchId: matchId || "",
    version: patch || "",
  };
};

export function useSummonerMatch(
  region: string,
  summonerName: string,
  patch: string,
  matchId: string,
  queryOptions: QueryHookOptions = {},
) {
  return useQuery<SummonerMatchData>(GET_SINGLE_MATCH_DATA, {
    ...queryOptions,
    variables: getSummonerMatchVariables(region, summonerName, patch, matchId),
  });
}

export function useLazySummonerMatch(
  region: string,
  summonerName: string,
  patch: string,
  matchId: string,
  queryOptions: QueryHookOptions = {},
) {
  return useLazyQuery(GET_SINGLE_MATCH_DATA, {
    ...queryOptions,
    variables: getSummonerMatchVariables(region, summonerName, patch, matchId),
  });
}

export const GET_MATCH_PAGE_EXTRA_DATA = gql`
  query matchPageExtraData(
    $matchIdString: String!,
    $matchIdInt: Int!,
    $regionId: String!,
    $summonerName: String!,
    $version: String!
  ) {
    fetchPostgameTags(
      matchId: $matchIdInt,
      regionId: $regionId,
      version: $version
    ) {
      summonerName
      tagList {
        ${Tag}
      }
    }
    fetchPerformanceScore(
      matchId: $matchIdString,
      regionId: $regionId,
      summonerName: $summonerName,
      version: $version
    ) {
      ${PerformanceScoreSchema}
    }
  }
`;

export interface MatchPageExtraData {
  fetchPostgameTags: Array<{
    summonerName: string;
    tagList: SummonerTag[];
  }>;
  fetchPerformanceScore: PerformanceScore[];
}

export function useMatchPageExtraData(
  summonerName: string,
  regionId: string,
  version: string,
  matchId: number,
  queryOptions: QueryHookOptions = {},
) {
  return useQuery<MatchPageExtraData>(GET_MATCH_PAGE_EXTRA_DATA, {
    ...queryOptions,
    variables: {
      summonerName: decodeURIComponent(summonerName),
      regionId,
      version,
      matchIdString: String(matchId),
      matchIdInt: matchId,
    },
  });
}
